import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import analyticsTrigger, { dispatchNewEvent } from "../../../utils/analytics/trigger/analyticsTrigger";
import { useFormStore } from "./components/context/store";
import useFormProgress from "./useFormProgress";
import { ELEMENTS, shouldShowErrorMessagePaymentMode, shouldShowPurchaseConditionsMessage } from "./utils";

export const usePageLoad = () => {
  const router = useRouter();
  useEffect(() => {
    analyticsTrigger("dataStateChange", router.pathname, {}, "pageload");
  }, []);
};

export const useFormDisqualification = state => {
  const hasTriggeredRef = useRef({
    purchaseConditions: false,
    errorMessagePaymentMode: false,
  });

  useEffect(() => {
    if (shouldShowPurchaseConditionsMessage(state) && !hasTriggeredRef?.current?.purchaseConditions) {
      dispatchNewEvent("dataStateChange", {
        action: "pricePromise",
        type: "form disqualification",
        subType: "Check you qualify",
      });
      hasTriggeredRef.current.purchaseConditions = true;
    }
  }, [state, hasTriggeredRef]);

  useEffect(() => {
    if (shouldShowErrorMessagePaymentMode(state) && !hasTriggeredRef?.current?.errorMessagePaymentMode) {
      dispatchNewEvent("dataStateChange", {
        action: "pricePromise",
        type: "form disqualification",
        subType: "Your original purchase",
      });
      hasTriggeredRef.current.errorMessagePaymentMode = true;
    }
  }, [state, hasTriggeredRef]);
};

const ELEMENTS_TITLE_MAPPING = {
  [ELEMENTS.ORDER_NUMBER]: "Order Number",
  [ELEMENTS.RECEIPT_PHOTOS]: "Your receipt",
  [ELEMENTS.JL_PRODUCT_CODE]: "John Lewis product code",
  [ELEMENTS.PRODUCT_NAME]: "Full product name",
  [ELEMENTS.PRODUCT_COLOR]: "productColor",
  [ELEMENTS.PRICE_PAID_AT_JL]: "Original price you paid at John Lewis",
  [ELEMENTS.PRICE_TO_MATCH]: "Price you'd like to match",
  [ELEMENTS.LINK_TO_LOWER_PRICES]: "Link to lower price item",
  [ELEMENTS.LOWER_PRICE_PHOTOS]: "Photo or screenshot of lower price",
  [ELEMENTS.USER_TITLE]: "Title",
  [ELEMENTS.FIRST_NAME]: "First name",
  [ELEMENTS.LAST_NAME]: "Last name",
  [ELEMENTS.PHONE]: "Phone number",
  [ELEMENTS.EMAIL]: "Email address",
  [ELEMENTS.POSTCODE]: "Postcode",
  "form-submission-failed": "Form submit failed",
};

const priceMatchErrors = errors => {
  dispatchNewEvent("dataStateChange", {
    action: "error",
    type: "priceMatch",
    errorType: "price promise form",
    error: errors,
  });
};

export const triggerPriceMatchErrors = errors => {
  const errorsForAnalytics = [];
  if (Object.keys(errors).length > 0) {
    // eslint-disable-next-line no-unused-vars
    Object.entries(errors).forEach(([key, _]) => {
      errorsForAnalytics.push({
        message: ELEMENTS_TITLE_MAPPING[key],
      });
    });
    priceMatchErrors(errorsForAnalytics);
  }
};

export const useConfirmationPageLoad = () => {
  const router = useRouter();

  const { reset, elementStates } = useFormStore(state => {
    // eslint-disable-next-line no-unused-vars
    const { updateResponses, addAttachment, removeAttachment, setAttachmentError, reset, ...remainingElementStates } =
      state;
    return {
      reset,
      elementStates: remainingElementStates,
    };
  });

  const pageModel = buildPageModel(elementStates);
  useEffect(() => {
    if (elementStates[ELEMENTS.PRICE_PAID_AT_JL] === "" && elementStates[ELEMENTS.PRICE_TO_MATCH] === "") {
      router.back();
      return;
    }
    analyticsTrigger("dataStateChange", router.pathname, pageModel, "pageload");
    reset();
  }, []);
};

const buildPageModel = state => {
  const pageModel = {
    priceMatch: {
      productPrice: state[ELEMENTS.PRICE_PAID_AT_JL],
      productCode: state[ELEMENTS.JL_PRODUCT_CODE],
      productName: state[ELEMENTS.PRODUCT_NAME],
      retailer: state[ELEMENTS.LOWER_PRICE_RETAILER],
      retailerPrice: state[ELEMENTS.PRICE_TO_MATCH],
      purchaseId: state[ELEMENTS.ORDER_NUMBER],
      purchaseType: state[ELEMENTS.PURCHASED_FROM],
      purchaseBranchName: state[ELEMENTS.PURCHASED_SHOP],
    },
  };
  return pageModel;
};

export const useFormProgressHandler = () => {
  const loggedSections = useRef(new Set());

  const handleVisibilityChange = useCallback(title => {
    if (!loggedSections.current.has(title)) {
      loggedSections.current.add(title);
      dispatchNewEvent("dataStateChange", {
        action: "pricePromise",
        type: "form progress",
        subType: title,
      });
    }
  }, []);

  useFormProgress(handleVisibilityChange);
};
